/**
 * Keeps direct navigation aligned with the permission-aware Insights navigation.
 * Denials reuse Root's existing application shell instead of rendering a nested full-page layout.
 */
import React from 'react';
import {
  canAccessInternalInsights,
  canAccessPublisherInsights,
  hasInsightsPermissionData,
} from 'src/hooks/insightsAccess';
import { useAllowedScope } from 'src/hooks/useAllowedScope';
import { useMyPermissions } from 'src/hooks/useMyPermissions';
import type { CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';
import { NoAccessContent } from './NoAccessPage';

interface InsightsAccessGuardProps {
  readonly children: React.ReactNode;
  readonly internalOnly?: boolean;
  readonly publisher?: CampaignDesiredStateDto['publisher'];
}

export const InsightsAccessGuard: React.FC<InsightsAccessGuardProps> = ({
  children,
  internalOnly = false,
  publisher,
}) => {
  const { assignments, isError, isLoading } = useMyPermissions();
  const { isLoading: isScopeLoading, publishers } = useAllowedScope('VIEW_CAMPAIGNS');

  if (isLoading || isScopeLoading) return null;

  const input = { assignments, isError, publishers };
  const canAccess = internalOnly
    ? canAccessInternalInsights(input)
    : publisher !== undefined
      ? canAccessPublisherInsights(input, publisher)
      : hasInsightsPermissionData(input);

  return canAccess ? <>{children}</> : <NoAccessContent />;
};
